import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { compileProgramme, type Diagnostic, type Programme } from '@tariff/authoring';
import { priceStay } from '@tariff/engine';
import { findUnreachableRates } from '@tariff/reachable';
import { findShadowedBands } from '@tariff/shadowed';
import { findPerNightRises } from '@rates/monotonic';
import type { Quote } from '@tariff/types';
import type { Period } from '@tariff/periods';
import { inferLadder, type LadderStep } from '@tariff/generator';
import { NightStrip, SummaryTiles } from './components/Quote';
import { PriceGrid } from './components/Grid';
import { CopyRates, RoundingSetting, type CopySource } from './components/CardTools';
import { Diagnostics, ProgrammeEditor, RateDetail, RateList } from './components/Programme';
import { Published } from './components/Published';
import { Matrix, type MatrixRow } from './components/Matrix';
import { Versions } from './components/Versions';
import { Rules } from './components/Rules';
import { NightCalendar } from './components/NightCalendar';
import { Settings } from './components/Settings';
import { Accounts } from './components/Accounts';
import { SignIn } from './components/SignIn';
import { Periods } from './components/Periods';
import { LadderReview } from './components/LadderReview';
import { Extras, Guests } from './components/Guests';
import { Units } from './components/Units';
import { Inquiries } from './components/Inquiries';
import { AccommodationForm } from './components/Accommodation';
import { KeyMapProvider, ShortcutHelp, useKeyMap } from './lib/keys';
import { api, ApiError, type AccommodationDto, type AttributeDefDto, type LadderProposalDto, type MeDto, type ParkSettingsDto, type VersionDto, type VersionSummaryDto } from './lib/api';
import { I18nProvider, LanguageSwitcher, SchemeSwitcher, useI18n, useT } from './lib/i18n';
import { applyDisplaySettings, toDay, toIso } from './lib/format';

type Tab = 'prices' | 'periods' | 'accommodation' | 'units' | 'rules' | 'inquiries' | 'settings' | 'accounts';
type Sub = 'quote' | 'calendar' | 'ladder' | 'guests' | 'programme' | 'published';

/**
 * Where a ladder starts when the card has none to read.
 *
 * A shape, not a recommendation: three nights, a week and a fortnight are the
 * lengths parks price, and the percentages are round numbers an operator will
 * replace. Module-level so the identity is stable and the seeding effect does not
 * fire on every render.
 */
const DEFAULT_LADDER: LadderStep[] = [
  { nights: 3, discountPct: 5 },
  { nights: 7, discountPct: 10 },
  { nights: 14, discountPct: 15 },
];

export default function App() {
  return (
    <I18nProvider>
      <KeyMapProvider>
        <Gate />
      </KeyMapProvider>
    </I18nProvider>
  );
}

/**
 * Is there a session?
 *
 * Asked once, before anything else loads. Everything the dashboard does needs a
 * credential, so loading the park first and then discovering it cannot read it
 * produces the screen this replaced: an empty park under a red banner, with
 * "Add them on the Periods tab first" about periods that exist and cannot be
 * seen.
 *
 * Three states, and `null` is the one that matters: it means *not asked yet*, and
 * rendering either the app or a login form during it would flash the wrong screen
 * at somebody who is signed in perfectly well.
 */
type Auth =
  | { signedIn: true; me: MeDto }
  | { signedIn: false; setupRequired: boolean }
  | null;

/**
 * What the signed-in account may do, for the panel to read.
 *
 * A COURTESY, not a control. The server decides on every request and will
 * refuse regardless of what this says; this exists so the panel can avoid
 * offering somebody a button that answers 403. Confusing the two would be the
 * expensive mistake, so it is worth saying plainly here: hiding a control
 * protects nobody, it only stops wasting their time.
 */
const CapabilityContext = createContext<readonly string[]>([]);

/** Whether the signed-in account holds a capability. `admin` holds them all. */
export function useCan(): (capability: string) => boolean {
  const held = useContext(CapabilityContext);
  return useCallback(
    (capability: string) => held.includes('admin') || held.includes(capability),
    [held],
  );
}

function Gate() {
  const [auth, setAuth] = useState<Auth>(null);

  const check = useCallback(async () => {
    try {
      const me = await api.me();
      setAuth({ signedIn: true, me });
    } catch (err) {
      const detail = err instanceof ApiError
        ? (err.detail as { setupRequired?: boolean } | undefined) : undefined;
      setAuth({ signedIn: false, setupRequired: detail?.setupRequired === true });
    }
  }, []);

  useEffect(() => { void check(); }, [check]);

  // Nothing at all until the answer is in. A spinner here would be a spinner on
  // every load of a screen that is usually instant.
  if (auth === null) return null;
  if (!auth.signedIn) {
    return <SignIn setupRequired={auth.setupRequired} onSignedIn={() => void check()} />;
  }
  return (
    <CapabilityContext.Provider value={auth.me.capabilities}>
      <Dashboard
        meId={auth.me.operatorId}
        onSignedOut={() => setAuth({ signedIn: false, setupRequired: false })}
      />
    </CapabilityContext.Provider>
  );
}

function Dashboard({ onSignedOut, meId }: { onSignedOut: () => void; meId?: string | null }) {
  const t = useT();
  const { lang } = useI18n();
  /**
   * The season on screen, remembered across reloads.
   *
   * It reset to the current year on every reload and every sign-in, which is
   * wrong for the two days a year this screen is used: authoring happens in
   * autumn FOR next spring, so the operator retyped the year after every refresh
   * — and after any mistake that reloaded the page.
   *
   * `localStorage` rather than the URL: the year is a working preference, not a
   * place, and putting it in the address bar would make a link somebody sends a
   * colleague carry their season rather than the one being discussed.
   */
  const [year, setYear] = useState(() => {
    try {
      const stored = Number(localStorage.getItem('vergarde.season'));
      // A sane year only. A corrupted value must not leave somebody staring at
      // an empty grid for the year 0.
      if (Number.isInteger(stored) && stored >= 2000 && stored <= 2100) return stored;
    } catch { /* private mode */ }
    return new Date().getUTCFullYear();
  });
  useEffect(() => {
    try { localStorage.setItem('vergarde.season', String(year)); } catch { /* private mode */ }
  }, [year]);
  const can = useCan();
  /*
    The first tab this account can actually open.

    `prices` for almost everybody, but an account scoped to enquiries alone
    would land on a tab that renders nothing and looks broken. Computed once
    at mount rather than watched, so switching tabs is never fought by a
    correction.
  */
  const [tab, setTab] = useState<Tab>(() => (can('catalogue.read') ? 'prices' : 'inquiries'));
  const [settings, setSettings] = useState<ParkSettingsDto | null>(null);
  const [parkName, setParkName] = useState('');
  const [sub, setSub] = useState<Sub>('quote');
  const [help, setHelp] = useState(false);

  const [accommodations, setAccommodations] = useState<AccommodationDto[]>([]);
  const [attributes, setAttributes] = useState<AttributeDefDto[]>([]);
  const [versions, setVersions] = useState<VersionSummaryDto[]>([]);
  const [periods, setPeriods] = useState<Period[]>([]);
  const [activeAcc, setActiveAcc] = useState<string | null>(null);
  const [activeVersion, setActiveVersion] = useState<string | null>(null);
  const [detail, setDetail] = useState<VersionDto | null>(null);
  const [editingAcc, setEditingAcc] = useState(false);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [arrival, setArrival] = useState<number>(0);
  const [nights, setNights] = useState(7);
  /*
    What the nights FIELD shows, which is not always a number.

    Kept beside `nights` rather than replacing it: the quote wants a number on
    every render, and the field wants to be empty for the moment between
    clearing it and typing the new length.
  */
  const [nightsText, setNightsText] = useState('7');
  const [qty, setQty] = useState<Record<string, number>>({});
/**
 * A longer stay that costs more per night than a shorter one.
 *
 * The operator's own worry, turned into something the panel can answer. A
 * channel consumes totals per (date, length) and cannot express how the park
 * arrived at them — so what a feed rejects, or renders as a price rise nobody
 * meant to advertise, is a length discount that goes backwards.
 *
 * The trap is that a park's instinct is about the MARGINAL night while the
 * channel's rule is about the AVERAGE, and the two can disagree completely.
 * This park's rentals are the case in point: nights four to seven cost about
 * EUR 29 each and nights eight onward about EUR 50, so the marginal price
 * doubles — and the average falls the whole way, EUR 93 at three nights to
 * EUR 50 at twenty-one. Nothing a channel could object to, on a shape
 * reasonably described as "the price goes up".
 */
function perNightRises(set: unknown, programme: Programme): Diagnostic[] {
  const quantities: Record<string, number> = {};
  for (const q of programme.quantities ?? []) {
    quantities[q.key] = (q.channel ?? q.key) === 'adult' ? 2 : 0;
  }
  const froms = programme.rates.map((r) => toDay(r.active.from) ?? 0);
  const untils = programme.rates.map((r) => toDay(r.active.until) ?? 0);
  if (froms.length === 0) return [];

  const rises = findPerNightRises(set as never, {
    // From today: a length curve in the past is not one anybody can be sold.
    from: Math.max(Math.min(...froms), Math.floor(Date.now() / 86_400_000)),
    until: Math.max(...untils),
    lengths: [1, 2, 3, 4, 5, 6, 7, 10, 14, 21],
    quantities,
    limit: 1,
  });
  if (rises.length === 0) return [];

  const r = rises[0]!;
  return [{
    severity: 'info',
    path: 'rates',
    code: 'per-night-rises',
    message: `A ${r.longer}-night stay costs more per night than a ${r.shorter}-night one `
      + `(${r.longerPerNight} against ${r.shorterPerNight}) for arrivals from `
      + `${toIso(r.arrival)}. The park may well want that, but a channel reads a `
      + 'length discount that goes backwards as a price rise, and some refuse the feed. '
      + 'It is the AVERAGE per night that matters here, not the price of the extra night.',
  }];
}

  const [selectedRate, setSelectedRate] = useState<string | null>(null);

  /**
   * From a price to the rule that made it.
   *
   * The operator's own words, after a week in the panel: Published rates and
   * Quote show what came out, and nothing leads back to what produced it. This
   * is the shortest true answer — a quote line already names the piece that
   * priced those nights, and a piece reduces to the rate an operator wrote.
   *
   * `requestAnimationFrame` rather than a straight scroll: the rates panel does
   * not exist until the sub-tab has rendered, so scrolling in the same tick
   * scrolls a document that has not changed yet.
   */
  const explainRate = useCallback((rateId: string) => {
    setSub('programme');
    setSelectedRate(rateId);
    setRateDirty(false);
    requestAnimationFrame(() => {
      document.getElementById('rate-detail')?.scrollIntoView({ block: 'start' });
    });
  }, []);
  /** An unsaved amount in the rate panel. Separate from the grid's own dirt. */
  const [rateDirty, setRateDirty] = useState(false);
  /** The card whose rules are being copied FROM, and its rules once fetched. */
  const [copyFrom, setCopyFrom] = useState('');
  const [copySource, setCopySource] = useState<VersionDto | null>(null);
  /**
   * A DEFAULT ladder, for a card that has none.
   *
   * It used to be the only ladder the editor ever showed. So an operator opening
   * a park's real card was shown three rungs belonging to no card at all — and
   * pressing Preview proposed adding eighteen rows and REMOVING the park's actual
   * long-stay packages, because the generator had never been told they existed.
   *
   * The panel underneath the editor promised, in so many words, that the ladder is
   * "read back from the existing rates, so it can start from the discounts already
   * in force rather than from invented ones". `inferLadder` does exactly that and
   * the editor above it ignored the answer.
   */
  const [steps, setSteps] = useState<LadderStep[]>(DEFAULT_LADDER);
  /**
   * What the ladder was last read from.
   *
   * The version ID alone was wrong. Seeding "once per version" meant that after
   * an operator edited the card — changed a week price, added a package — the
   * Ladder tab still showed the rungs read from the card BEFORE the edit, and
   * Preview then proposed changes measured against prices that no longer
   * existed. A signature over what the ladder actually reads from re-seeds when
   * that changes and stays quiet when anything else does.
   */
  const seededFrom = useRef<string | null>(null);
  /** Set once the operator edits a rung by hand, so re-seeding cannot wipe it. */
  const [stepsDirty, setStepsDirty] = useState(false);
  /** True when the card has moved under a ladder the operator is editing. */
  const [ladderStale, setLadderStale] = useState(false);
  /** Where the rungs on screen came from. See the seeding effect. */
  const [ladderBasis, setLadderBasis] = useState<'card' | 'premium' | 'nothing'>('card');
  const [proposal, setProposal] = useState<LadderProposalDto | null>(null);

  /**
   * Seed the ladder from the card the operator just opened.
   *
   * Once per version, and only from what the card actually charges. A card with
   * no length-of-stay rates at all infers nothing, and then the default is the
   * right answer — it is a starting point rather than a claim about this park.
   */
  /**
   * Follow the season selector on the accommodation screen.
   *
   * The header control changed `year`, the prices grid and the periods list
   * followed it, and this tab did not: the version panel lists every season an
   * accommodation has, and the selected one never moved. So switching from 2026
   * to 2027 while looking at a card did nothing at all on screen — reported, in
   * exactly those words, as "changing it nothing happens".
   *
   * Nothing is chosen when the accommodation has no card for that season. The
   * alternative — leaving last season's card selected under a header saying 2027
   * — is the one outcome that actively misleads.
   */
  const followedSeason = useRef<string | null>(null);
  useEffect(() => {
    if (!activeAcc) return;
    /**
     * Only when the SEASON (or the accommodation) actually changed.
     *
     * The first version of this ran on every render and re-picked the season's
     * card each time — so clicking a 2025 chip while the header said 2026 was
     * undone before the operator's finger left the mouse. A click is an explicit
     * instruction and outranks a filter; the header follows it instead, in
     * `chooseVersion` below. This effect exists only to answer "I changed the
     * season, show me that season".
     */
    const key = `${year}|${activeAcc}`;
    if (followedSeason.current === key) return;
    followedSeason.current = key;

    const mine = versions.filter((v) => v.accommodationId === activeAcc);
    if (mine.length === 0) return;
    const current = mine.find((v) => v.id === activeVersion);
    if (current && current.seasonYear === year) return;
    const forSeason = mine.filter((v) => v.seasonYear === year);
    // The published one first: on a season with a draft beside a live card, the
    // live card is what somebody asking for that season means.
    const pick = forSeason.find((v) => v.state === 'published') ?? forSeason[0] ?? null;
    setActiveVersion(pick?.id ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, activeAcc, versions]);

  /**
   * Pick a version, and let the header catch up.
   *
   * Selecting last season's card is a perfectly ordinary thing to do — it is how
   * you look at what you charged, and how you clone it forward — and the season
   * box is a filter, not a permission. So the click wins and the filter moves to
   * match it, which also stops the two controls from disagreeing on screen.
   */
  const chooseVersion = useCallback((id: string) => {
    setActiveVersion(id);
    const v = versions.find((x) => x.id === id);
    if (v && v.seasonYear !== year) {
      // Marked as already followed, or the effect above would see a season
      // change and immediately re-pick that season's card instead.
      followedSeason.current = `${v.seasonYear}|${v.accommodationId}`;
      setYear(v.seasonYear);
    }
  }, [versions, year]);

  const ladderSignature = useMemo(() => (detail
    ? `${detail.id}|${detail.programme.rates
      .map((r) => `${r.id}:${r.role ?? ''}:${r.nights}:${r.price?.base ?? ''}`).join(',')}`
    : null), [detail]);

  useEffect(() => {
    if (!detail || ladderSignature === null) return;
    if (seededFrom.current === ladderSignature) return;
    const firstForThisVersion = !seededFrom.current?.startsWith(`${detail.id}|`);
    seededFrom.current = ladderSignature;
    // Hand-edited rungs are the operator's work and are never overwritten by a
    // re-read. They are TOLD the card moved instead, and can re-read on purpose.
    if (stepsDirty && !firstForThisVersion) { setLadderStale(true); return; }
    setStepsDirty(false);
    setLadderStale(false);
    /**
     * What the card already charges, filtered to what a LADDER can express.
     *
     * `inferLadder` answers a broader question — every non-nightly rate read as a
     * discount — and two of its answers are not ladder rungs:
     *
     *   - a season package of 84 or 98 nights, which the API refuses outright
     *     (`nights` must be 1..90), so Preview returned 400 every time and the
     *     tab was dead on the park's real card;
     *   - an arrangement priced ABOVE the nightly rate, which came out as
     *     "-37.9% off". A premium package is not a length discount, and offering
     *     to regenerate it as one would reprice it downwards.
     */
    const all = inferLadder(detail.programme);
    const observed = all.filter((o) => o.nights >= 2 && o.nights <= 90 && o.discountPct > 0);
    /**
     * Why these rungs, in one word, for the panel to explain.
     *
     * The park's question was "why is the ladder so short compared to the amount
     * of tariffs?" — asked of a twelve-rate card showing three rungs. The three
     * were the DEFAULT, invented, and nothing on screen said so. Two different
     * things lead there and they want different answers:
     *
     *   nothing   the card has no multi-night rates to read at all
     *   premium   it has them and they cost MORE than the nights they cover, so
     *             they are packages rather than length discounts
     */
    setLadderBasis(observed.length ? 'card' : (all.length ? 'premium' : 'nothing'));
    setSteps(observed.length
      ? observed.map((o) => ({ nights: o.nights, discountPct: o.discountPct }))
      : DEFAULT_LADDER);
    setProposal(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail, ladderSignature]);

  /**
   * Run one API call, with the banner it deserves.
   *
   * The success notice may be a FUNCTION of the result, because some of them are
   * only knowable once the server has answered: "added 47 units" depends on how
   * many of a range were free, and "3 versions are now live" on what publishing
   * this one displaced.
   */
  const run = useCallback(async <T,>(
    fn: () => Promise<T>, ok?: string | ((out: T) => string),
  ): Promise<T | null> => {
    setBusy(true); setError(null);
    try {
      const out = await fn();
      if (ok) setNotice(typeof ok === 'function' ? ok(out) : ok);
      return out;
    } catch (e) {
      // The API writes its errors for a person to read, so show them rather
      // than a status code. They arrive in the API's own language, which the
      // dashboard cannot translate — but when there is no message at all, the
      // one sentence this side supplies is the operator's.
      if (e instanceof ApiError) {
        setError(e.message || t('app.requestFailed', {
          method: e.request?.method ?? '', path: e.request?.path ?? '',
        }));
      } else {
        setError(String(e));
      }
      return null;
    } finally {
      setBusy(false);
    }
  }, [t]);

  const reload = useCallback(async () => {
    const [accs, vers, pers, attrs, settings] = await Promise.all([
      // The season on screen, so "2026 live" is not what a 2027 screen says.
      api.accommodations(year), api.versions(), api.periods(), api.attributes(),
      api.parkSettings(),
    ]);
    setAccommodations(accs);
    setVersions(vers);
    setPeriods(pers);
    setAttributes(attrs);
    // Best effort: a header that says nothing is better than a screen that fails
    // to load because a name could not be fetched.
    void api.parks().then((list) => {
      if (list.length) setParkName(list[0].label);
    }).catch(() => {});
    // Applied before anything renders a date, because a screen that draws once in
    // one format and again in another is a screen an operator distrusts.
    //
    // The interface language is the date order's default; the park's own
    // `dateFormat` overrides it for the park whose conventions and language
    // disagree. Nothing about STORAGE is involved — see lib/format.ts.
    setSettings(settings);
    return { accs, vers };
    // `year`, because the accommodation list reports which card is live and
    // that answer differs per season on a park with two published at once.
  }, [year]);

  useEffect(() => { void run(reload); }, [run, reload]);

  /**
   * Re-derive the formatters when the park's settings or the language changes.
   *
   * Both are inputs: the language decides the date order unless the park has
   * overridden it, so switching to Dutch must move the dates with it. Kept out of
   * `reload` so changing language does not refetch the catalogue.
   */
  useEffect(() => {
    applyDisplaySettings({ ...(settings ?? {}), locale: lang });
  }, [settings, lang]);

  /* Follow the selected version into its detail, and reset the quote inputs. */
  useEffect(() => {
    if (!activeVersion) { setDetail(null); return; }
    void run(async () => {
      const v = await api.version(activeVersion);
      setDetail(v);
      const d = toDay(v.programme.defaultArrival ?? '') ?? firstActive(v.programme);
      setArrival(d);
      const next: Record<string, number> = {};
      for (const q of v.programme.quantities ?? []) next[q.key] = 0;
      setQty(next);
      setSelectedRate(null);
      // Unsaved amounts belong to the rate they were typed into. Carrying the
      // flag to the next version left a Save button offering to write a change
      // that was no longer on screen.
      setRateDirty(false);
      setProposal(null);
      return v;
    });
  }, [activeVersion, run]);

  const compiled = useMemo(
    () => {
      if (!detail) return null;
      const c = compileProgramme(detail.programme);
      if (!c.ok) return c;
      /**
       * An offer nobody can ever be given is not a compile error.
       *
       * Nothing about a two-week rate priced above fourteen single nights is
       * malformed — it has a name, a window and a price, and it reads as a
       * discount to everyone in the office. It is only visible by asking the
       * engine whether any stay it was written for actually uses it, which is a
       * question the compiler does not ask, so the answer is merged in here
       * beside the checks that come from the same panel.
       */
      return {
        ...c,
        diagnostics: [
          ...c.diagnostics,
          ...findUnreachableRates(detail.programme, { set: c.set }),
          // Structural, and quiet: three findings across the whole park, all of
          // them a day rate the cheapest tiling can never reach.
          ...findShadowedBands(detail.programme),
          // The one question a channel actually asks of a length curve.
          ...perNightRises(c.set, detail.programme),
        ],
      };
    },
    [detail],
  );

  const quote: Quote | null = useMemo(() => {
    if (!compiled || !arrival) return null;
    try {
      // Priced locally: the engine answers in under a millisecond here, so a
      // round trip would be slower than the search itself.
      return priceStay(compiled.set, { arrival, nights, quantities: qty });
    } catch { return null; }
  }, [compiled, arrival, nights, qty]);

  const accVersions = useMemo(
    () => versions.filter((v) => v.accommodationId === activeAcc),
    [versions, activeAcc],
  );

  const activeAccommodation = useMemo(
    () => accommodations.find((a) => a.id === activeAcc) ?? null,
    [accommodations, activeAcc],
  );

  /* Rows of the matrix: one draft per accommodation for the chosen season. */
  /**
   * The most recent season with cards, when THIS one has none.
   *
   * Null when there is nothing to copy — a park's first season — and then no
   * button is offered at all: one that cannot work is worse than none.
   */
  const rollForwardFrom = useMemo(() => {
    const here = versions.some((v) => v.seasonYear === year && v.state !== 'archived');
    if (here) return null;
    const earlier = versions
      .filter((v) => v.seasonYear < year && v.state !== 'archived')
      .map((v) => v.seasonYear);
    return earlier.length ? Math.max(...earlier) : null;
  }, [versions, year]);

  /**
   * The price grid's unsaved edits.
   *
   * Held here rather than inside `Matrix`, so switching to another tab and back
   * does not throw them away. It used to: the pill said "1 unsaved", the operator
   * went to look something up, came back, and the price was gone — no warning, no
   * trace, nothing to report.
   */
  const [matrixDraft, setMatrixDraft] = useState<Record<string, Programme>>({});

  const [matrixRows, setMatrixRows] = useState<MatrixRow[]>([]);
  useEffect(() => {
    let cancelled = false;
    const wanted = versions.filter((v) => v.seasonYear === year && v.state !== 'archived');
    void (async () => {
      const loaded = await Promise.all(wanted.map(async (v) => {
        const full = await api.version(v.id).catch(() => null);
        if (!full) return null;
        const acc = accommodations.find((a) => a.id === v.accommodationId);
        return {
          versionId: v.id,
          accommodationId: v.accommodationId,
          label: `${acc?.label ?? v.accommodationId} · ${v.label}`,
          name: acc?.label ?? v.accommodationId,
          versionLabel: v.label,
          programme: full.programme,
          readOnly: v.state === 'published',
        } satisfies MatrixRow;
      }));
      if (!cancelled) setMatrixRows(loaded.filter((r): r is MatrixRow => r !== null));
    })();
    return () => { cancelled = true; };
  }, [versions, accommodations, year]);

  useKeyMap(useMemo(() => [
    { combo: '?', label: 'keys.showShortcuts', group: 'keys.groupGeneral', run: () => setHelp((h) => !h) },
    { combo: 'escape', label: 'keys.closeOverlay', group: 'keys.groupGeneral', run: () => setHelp(false), when: () => help },
    { combo: 'g', label: 'keys.goToPrices', group: 'keys.groupNavigate', run: () => setTab('prices') },
    { combo: 'p', label: 'keys.goToPeriods', group: 'keys.groupNavigate', run: () => setTab('periods') },
    { combo: 'a', label: 'keys.goToAccommodation', group: 'keys.groupNavigate', run: () => setTab('accommodation') },
    { combo: 'u', label: 'keys.goToUnits', group: 'keys.groupNavigate', run: () => setTab('units') },
    { combo: 'e', label: 'keys.goToInquiries', group: 'keys.groupNavigate', run: () => setTab('inquiries') },
    { combo: 'r', label: 'keys.goToRules', group: 'keys.groupNavigate', run: () => setTab('rules') },
    { combo: 's', label: 'keys.goToSettings', group: 'keys.groupNavigate', run: () => setTab('settings') },
  ], [help]));

  /**
   * The seasons worth offering: the ones that exist, plus one to start.
   *
   * Built from the versions AND the periods, because the two arrive at different
   * moments — a season whose periods are defined but whose first card is not yet
   * written is exactly the season somebody is about to work on.
   */
  const seasonChoices = useMemo(() => {
    const known = [year, new Date().getUTCFullYear()];
    for (const v of versions) known.push(v.seasonYear);
    for (const p of periods) known.push(p.year);
    const first = Math.min(...known);
    // Three beyond the last, because a park plans further ahead than the data it
    // already has: the season after next is a real thing to start, and a list
    // that stops at "one more than exists" makes it unreachable.
    const last = Math.max(...known) + 3;
    // Contiguous, not the sparse set of years that happen to have data. A
    // dropdown reading 2024, 2026, 2029 invites the question of what happened to
    // the others, and the answer — nothing — is not worth asking.
    return Array.from({ length: last - first + 1 }, (_, i) => first + i);
  }, [versions, periods, year]);

  /**
   * The cards this one could take rules from: the same season, a different
   * accommodation, and only ones that HAVE rules.
   *
   * Same season deliberately. A rule carries its dates, so copying a 2026 rule
   * into a 2027 card lands it a year out while looking perfectly correct in the
   * list. Carrying a card forward is a different operation with its own answer
   * — `cloneProgramme` shifts every window — and it is on the versions screen.
   */
  const copySources = useMemo<CopySource[]>(() => {
    if (!detail) return [];
    const named = new Map(accommodations.map((a) => [a.id, a.label]));
    // ONE ENTRY PER ACCOMMODATION. A season often holds a draft beside a
    // published card, and two options reading "Zomerlodge" is a choice nobody
    // can make. The published one wins — it is the card that is actually in
    // use, and so the one whose rules are worth spreading.
    const best = new Map<string, typeof versions[number]>();
    for (const v of versions) {
      if (v.id === detail.id) continue;
      if (v.seasonYear !== detail.seasonYear) continue;
      if (v.accommodationId === detail.accommodationId) continue;
      const had = best.get(v.accommodationId);
      if (!had || (had.state !== 'published' && v.state === 'published')) {
        best.set(v.accommodationId, v);
      }
    }
    return [...best.values()]
      .map((v) => ({
        id: v.id,
        accommodationId: v.accommodationId,
        accommodationLabel: named.get(v.accommodationId) ?? v.accommodationId,
        label: v.label,
      }))
      .sort((a, b) => a.accommodationLabel.localeCompare(b.accommodationLabel));
  }, [detail, versions, accommodations]);

  const periodsForYear = useMemo(
    () => periods.filter((p) => p.year === year)
      .sort((a, b) => a.from.localeCompare(b.from)),
    [periods, year],
  );

  return (
    <main className="shell">
      <header className="head">
        <div>
          {/* Proper nouns, both of them: the operator's registered name and the
              product's. Neither belongs in a catalogue — translating a park's
              name would show it something it never called itself — but the park
              name IS hardcoded here, and belongs in per-park configuration the
              day a second park exists. */}
          {/* The park's own registered name, read from the server.
              It was hardcoded — correct for the park this was written for and
              wrong the day a second one exists, in the one place somebody would
              look to check which park they are editing. */}
          <div className="eyebrow">{parkName}</div>
          <h1>Tariff Studio</h1>
        </div>
        <span className="grow" />
        {/**
          * A LIST of seasons, not a number to retype.
          *
          * It was `<input type="number" value={year}>` with `Number(x) || year`
          * on change, which cannot be cleared: emptying the box parses to 0,
          * falls back to the year it already held, and re-renders the old value
          * — so typing a new one meant landing the caret exactly right and
          * overtyping four digits, and on a phone it simply did not work. The
          * park reported it as "editing the season does not go from 2026 to
          * 2027", which is precisely what it did.
          *
          * A picker is also the better control on its own merits. Seasons are a
          * known, small set — the ones with a card, plus room to start the next
          * — and a spinner offers 1970 and 9999 with equal enthusiasm.
          */}
        <div className="field" style={{ maxWidth: 130 }}>
          <label htmlFor="season">{t('app.season')}</label>
          <select
            id="season"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          >
            {seasonChoices.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
        <LanguageSwitcher />
        <SchemeSwitcher />
        {/* A shared office machine needs a way off it. Beside the preferences
            rather than buried, because the person who needs it is leaving. */}
        <button
          type="button"
          className="btn"
          onClick={() => { void api.logout().catch(() => {}).then(onSignedOut); }}
        >
          {t('signin.signOut')}
        </button>
        <button type="button" className="btn" onClick={() => setHelp(true)} title={t('keys.title')}>
          ?
        </button>
      </header>

      {error && (
        <div className="banner bad" role="alert">
          {error}
          <button type="button" className="btn" onClick={() => setError(null)}>
            {t('app.dismiss')}
          </button>
        </div>
      )}
      {notice && (
        <div className="banner" role="status">
          {notice}
          <button type="button" className="btn" onClick={() => setNotice(null)}>
            {t('app.acknowledge')}
          </button>
        </div>
      )}

      {/*
        THE TABS SOMEBODY CAN ACTUALLY USE.

        Reception holds `catalogue.read`, `inquiry.read` and `block.write` — so
        prices are worth showing (they may look, and looking is the job) while
        Settings and Accounts are not, because every control on them answers
        403. A tab that exists only to refuse is worse than an absent one: it
        reads as the software being broken rather than as the account being
        scoped.

        Each entry names the capability the tab's CONTENT needs, which is why
        `prices` asks for `catalogue.read` rather than `version.write` — the
        editing inside it is gated separately, further down.
      */}
      <div className="tabs" role="tablist">
        {([
          ['prices', 'app.tabPrices', 'catalogue.read'],
          ['periods', 'app.tabPeriods', 'catalogue.read'],
          ['accommodation', 'app.tabAccommodation', 'catalogue.read'],
          ['units', 'app.tabUnits', 'catalogue.read'],
          ['rules', 'app.tabRules', 'catalogue.read'],
          ['inquiries', 'app.tabInquiries', 'inquiry.read'],
          ['settings', 'app.tabSettings', 'park.write'],
          ['accounts', 'app.tabAccounts', 'admin'],
        ] as const)
          .filter(([, , need]) => can(need))
          .map(([id, label]) => (
            <button
              key={id} type="button" role="tab" className="tab"
              aria-selected={tab === id} onClick={() => setTab(id)}
            >
              {t(label)}
            </button>
          ))}
      </div>

      {tab === 'prices' && (
        <section className="panel">
          <div className="panel-hd">
            <h2>{t('app.pricesHeading', { year })}</h2>
            <span className="grow" />
            {/* Two counts, two plurals, one separator between them. A single
                message cannot carry two independent plurals without a rule
                engine, and the bullet is punctuation rather than grammar, so
                joining here costs a translator nothing. */}
            <span className="note">
              {t('app.accommodationCount', { count: matrixRows.length })}
              {' · '}
              {t('app.periodCount', { count: periodsForYear.length })}
            </span>
          </div>
          <div className="panel-bd">
            <Matrix
              rows={matrixRows}
              periods={periodsForYear}
              saving={busy}
              // Reading a price is most of what a desk does; changing one is
              // not reception's job and the server refuses it either way.
              readOnly={!can('version.write')}
              /**
               * From the period grid to one night of it.
               *
               * These are two different questions — "what does this period
               * cost" and "what does this Saturday cost" — and the second one
               * lived on another tab behind an accommodation picker, which is
               * nowhere anybody would look for it while staring at a grid of
               * prices. The accommodation's name is the door.
               */
              onOpenNights={({ accommodationId, versionId }) => {
                setActiveAcc(accommodationId);
                setActiveVersion(versionId);
                setTab('accommodation');
                // Two levels down, not one: the calendar is a sub-tab of the
                // accommodation screen. Landing on the accommodation's quote
                // panel would be the same dead end one click further along.
                setSub('calendar');
                // The calendar sits below the fold on a phone, and landing on
                // the top of a tab you did not ask for reads as nothing having
                // happened. Deferred a frame so the panel exists to scroll to.
                requestAnimationFrame(() => {
                  document.getElementById('night-calendar')
                    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                });
              }}
              draft={matrixDraft}
              onDraftChange={setMatrixDraft}
              rollForwardFrom={rollForwardFrom}
              rollForwardTo={year}
              onRollForward={() => {
                if (rollForwardFrom == null) return;
                void run(async () => {
                  // Every card of that season that is not archived, cloned a year
                  // on. Sequential rather than parallel: these are writes to one
                  // SQLite file, and a park has a handful of accommodations.
                  const source = versions.filter(
                    (v) => v.seasonYear === rollForwardFrom && v.state !== 'archived',
                  );
                  // What is already here, tracked as we go.
                  //
                  // Reading `versions` inside the loop reads the state as it was
                  // when this handler was created, so a second press — before the
                  // reload lands — saw an empty park and asked the API to create
                  // everything again. Only the database's uniqueness stopped it,
                  // and the operator got `version "trekkershut-2027" already
                  // exists` in red beside the success banner.
                  const already = new Set(
                    versions.filter((v) => v.seasonYear === year)
                      .map((v) => v.accommodationId),
                  );
                  let made = 0;
                  for (const v of source) {
                    if (already.has(v.accommodationId)) continue;
                    already.add(v.accommodationId);
                    await api.cloneVersion(v.id, {
                      id: `${v.accommodationId}-${year}`,
                      // The card's own name with the year moved, not a bare
                      // "2027": the chip bar otherwise reads 2027 / 2027 / DRAFT
                      // beside Comfortplaats 2026 / PUBLISHED.
                      label: v.label.includes(String(rollForwardFrom))
                        ? v.label.replace(String(rollForwardFrom), String(year))
                        : `${v.label} ${year}`,
                      seasonYear: year,
                      // 364 days PER YEAR CROSSED, not a flat 364.
                      //
                      // Hardcoded, copying 2027 into 2029 produced 2029 cards
                      // whose rates were dated 2028 — a card that cannot price
                      // anything in its own season, and nothing on screen saying
                      // so. 364 rather than 365 keeps the weekdays aligned, which
                      // is what a park with Saturday changeovers means by "a year
                      // on"; across two years that is 728.
                      shiftDays: 364 * (year - rollForwardFrom),
                    });
                    made += 1;
                  }
                  await reload();
                  return made;
                }, (made) => t('matrix.rolledForward', { count: made }));
              }}
              onSave={async (changed) => {
                await run(async () => {
                  for (const c of changed) {
                    await api.updateVersion(c.versionId, { programme: c.programme });
                  }
                  await reload();
                  return true;
                }, t('app.savedAccommodations', { count: changed.length }));
              }}
            />
          </div>
        </section>
      )}

      {tab === 'periods' && (
        <Periods
          periods={periods}
          year={year}
          onYear={setYear}
          busy={busy}
          onSeed={(y) => { void run(async () => { await api.seedPeriods(y); await reload(); return true; }, t('app.periodsSeeded')); }}
          onSave={(p) => { void run(async () => { await api.putPeriod(p); await reload(); return true; }, t('app.periodSaved')); }}
          onDelete={(id) => { void run(async () => { await api.deletePeriod(id); await reload(); return true; }, t('app.periodDeleted')); }}
        />
      )}

      {tab === 'accommodation' && (
        <>
          <section className="panel">
            <div className="panel-hd">
              <h2>{t('app.accommodationHeading')}</h2>
              <span className="grow" />
              <button
                type="button" className="btn"
                onClick={() => {
                  // A starting label, not a translated one: it becomes DATA the
                  // moment it is saved, and the operator will rename it. It
                  // starts in their language because they are the one who has
                  // to read it before they get round to that.
                  const number = accommodations.length + 1;
                  const label = t('app.newAccommodationLabel', { number });
                  // The id is an identifier and stays English. It used to be
                  // slugged from the label, which was fine while the label was
                  // English and would have quietly produced `----3` the first
                  // time a language that is not written in ASCII arrived.
                  const id = `accommodation-${number}`;
                  void run(async () => {
                    await api.putAccommodation(id, { label });
                    await api.createVersion({
                      id: `${id}-${year}`, accommodationId: id, label: String(year), seasonYear: year,
                    });
                    const r = await reload();
                    setActiveAcc(id);
                    setActiveVersion(`${id}-${year}`);
                    return r;
                  }, t('app.accommodationCreated'));
                }}
              >
                {t('app.addAccommodation')}
              </button>
              {/* The rename that was missing entirely: every accommodation kept
                  the label it was created with, "New accommodation 7" and all. */}
              <button
                type="button" className="btn" disabled={!activeAcc || busy}
                onClick={() => setEditingAcc((v) => !v)}
              >
                {t('accommodation.edit')}
              </button>
            </div>
            <div className="panel-bd">
              <div className="controls">
                <div className="field" style={{ gridColumn: 'span 2' }}>
                  <label htmlFor="acc">{t('app.accommodationHeading')}</label>
                  <select
                    id="acc"
                    value={activeAcc ?? ''}
                    onChange={(e) => {
                      setActiveAcc(e.target.value);
                      setEditingAcc(false);
                      const first = versions.find((v) => v.accommodationId === e.target.value);
                      setActiveVersion(first?.id ?? null);
                    }}
                  >
                    <option value="">{t('app.chooseAccommodation')}</option>
                    {accommodations.map((a) => (
                      // Three facts with bullets between them, not a sentence:
                      // the name is data, the count is a plural, and the live
                      // state is its own clause. Written as one string it would
                      // be a sentence no translator could reorder.
                      <option key={a.id} value={a.id}>
                        {a.label}
                        {' · '}
                        {t('app.versionCount', { count: a.versions })}
                        {' · '}
                        {a.published
                          ? t('app.liveVersion', { label: a.published.label })
                          : a.draft
                            ? t('app.draftVersion', { label: a.draft.label })
                            : t('app.nothingLive')}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {editingAcc && activeAccommodation && (
                <AccommodationForm
                  accommodation={activeAccommodation}
                  attributes={attributes}
                  busy={busy}
                  onCancel={() => setEditingAcc(false)}
                  onSubmit={(patch) => {
                    void run(async () => {
                      await api.putAccommodation(activeAccommodation.id, patch);
                      await reload();
                      return true;
                    }, t('app.accommodationSaved'));
                    setEditingAcc(false);
                  }}
                />
              )}

              {activeAcc && (
                <div style={{ marginTop: 16 }}>
                  <Versions
                    versions={accVersions}
                    accommodationId={activeAcc}
                    accommodationLabel={activeAccommodation?.label ?? activeAcc}
                    year={year}
                    activeId={activeVersion}
                    busy={busy}
                    onSelect={chooseVersion}
                    onCreate={(v) => {
                      void run(async () => {
                        await api.createVersion({ ...v, accommodationId: activeAcc });
                        await reload();
                        setActiveVersion(v.id);
                        return true;
                      }, t('app.versionCreated'));
                    }}
                    onResolve={async (arrival) => {
                      const found = await run(
                        () => api.versionForArrival(activeAcc, arrival),
                      );
                      return found?.versionId ?? null;
                    }}
                    onClone={(id, o) => {
                      void run(async () => {
                        await api.cloneVersion(id, {
                          id: o.id, label: o.label, seasonYear: o.seasonYear,
                          shiftDays: o.shiftDays,
                          dropRoles: o.dropLadder ? ['ladder'] : undefined,
                        });
                        await reload();
                        setActiveVersion(o.id);
                        return true;
                      }, t('app.clonedInto', { label: o.label }));
                    }}
                    onPublish={(id, window) => {
                      void run(async () => {
                        const r = await api.publishVersion(id, window);
                        await reload();
                        return r;
                      }, (r) => {
                        // What the notice used to say — "this is now the version
                        // guests are quoted from" — stopped being true the moment
                        // a second season went live, and the demo park already
                        // has two. So it is assembled from what the server
                        // actually did: which dates this card covers, what it
                        // displaced, and what else stays live beside it.
                        const parts = [t('app.published')];
                        const mine = r.live?.find((l) => l.id === id);
                        if (mine?.validFrom && mine.validUntil) {
                          parts.push(t('app.publishedForDates', {
                            from: mine.validFrom, until: mine.validUntil,
                          }));
                        }
                        if (r.replaced) {
                          parts.push(t('app.publishedReplaced', { label: r.replaced.label }));
                        }
                        if (r.live) {
                          parts.push(t('app.publishedAlsoLive', { count: r.live.length }));
                        }
                        return parts.join(' ');
                      });
                    }}
                    onArchive={(id) => {
                      void run(async () => { await api.setVersionState(id, 'archived'); await reload(); return true; }, t('app.archived'));
                    }}
                    onRename={(id, label) => {
                      void run(async () => {
                        await api.updateVersion(id, { label });
                        await reload();
                        return true;
                      }, t('app.renamed'));
                    }}
                    onDelete={(id) => {
                      void run(async () => {
                        await api.deleteVersion(id);
                        const r = await reload();
                        setActiveVersion(null);
                        return r;
                      }, t('app.deleted'));
                    }}
                  />
                </div>
              )}
            </div>
          </section>

          {detail && compiled && (
            <>
              <div className="tabs" role="tablist">
                {/*
                  Reception may LOOK at prices and may not author them.

                  `quote`, `calendar` and `published` answer "what does this
                  cost" — which is the whole reason somebody at a desk opens
                  this tab, and is a read. `ladder`, `guests` and `programme`
                  are the authoring surface: every control on them writes, so
                  showing them to an account that cannot write is showing a
                  screen whose every button refuses.
                */}
                {([['quote', 'app.subQuote', 'catalogue.read'], ['calendar', 'calendar.tab', 'catalogue.read'], ['ladder', 'app.subLadder', 'version.write'], ['guests', 'app.subGuests', 'version.write'], ['programme', 'app.subProgramme', 'version.write'], ['published', 'app.subPublished', 'catalogue.read']] as const)
                  .filter(([, , need]) => can(need))
                  .map(([id, label]) => (
                    <button
                      key={id} type="button" role="tab" className="tab"
                      aria-selected={sub === id} onClick={() => setSub(id)}
                    >
                      {t(label)}
                    </button>
                  ))}
              </div>

              {sub === 'quote' && (
                <>
                  <section className="panel">
                    <div className="panel-bd">
                      <div className="controls">
                        <div className="field">
                          <label htmlFor="arr">{t('app.arrival')}</label>
                          {/*
                            Bounded to the arrivals this card actually prices.

                            A 2027 card asked about a 2026 arrival answers with
                            nothing, and the screen then reads as a broken
                            quote rather than as a date outside the card. The
                            picker opens on the window instead, so the browser's
                            own calendar refuses the impossible dates and there
                            is nothing to explain.
                          */}
                          <input
                            id="arr" type="date" value={arrival ? toIso(arrival) : ''}
                            min={detail.validFrom != null ? toIso(detail.validFrom) : undefined}
                            max={detail.validUntil != null ? toIso(detail.validUntil - 1) : undefined}
                            onChange={(e) => { const d = toDay(e.target.value); if (d !== null) setArrival(d); }}
                          />
                        </div>
                        <div className="field">
                          <label htmlFor="nts">{t('app.nights')}</label>
                          {/*
                            The field may be EMPTY while somebody retypes it.

                            It held the number directly and coerced on every
                            keystroke, so clearing it produced `Number('') || 1`
                            and the box refilled itself with 1 before the second
                            digit arrived. Typing 14 was impossible without
                            selecting the 1 first, which nobody thinks to do.

                            The text is what the field shows; the number is what
                            the quote uses, and it only moves when the text is a
                            usable one.
                          */}
                          <input
                            id="nts" type="number" min={1} max={35} value={nightsText}
                            onChange={(e) => {
                              const raw = e.target.value;
                              setNightsText(raw);
                              const n = Number(raw);
                              if (raw !== '' && Number.isFinite(n) && n >= 1) {
                                setNights(Math.min(35, Math.floor(n)));
                              }
                            }}
                            onBlur={() => setNightsText(String(nights))}
                          />
                        </div>
                        {(detail.programme.quantities ?? []).map((q) => (
                          <div className="field" key={q.key}>
                            <label htmlFor={`q-${q.key}`}>{q.label}</label>
                            <input
                              id={`q-${q.key}`} type="number" min={0} max={20} value={qty[q.key] ?? 0}
                              onChange={(e) => setQty({ ...qty, [q.key]: Math.max(0, Number(e.target.value) || 0) })}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </section>
                  <SummaryTiles quote={quote} nights={nights} arrival={arrival} />
                  {quote && quote.extras && quote.extras.length > 0 && (
                    <section className="panel">
                      {/* Extras are applied to the winning tiling AFTER the
                          search; they are never searched over. That is engine
                          behaviour, not something the operator has to read on
                          the panel header. */}
                      <div className="panel-hd">
                        <h2>{t('app.extrasHeading')}</h2>
                      </div>
                      <div className="panel-bd">
                        <Extras
                          extras={quote.extras}
                          extrasTotal={quote.extrasTotal}
                          accommodationTotal={quote.accommodationTotal}
                        />
                      </div>
                    </section>
                  )}
                  <section className="panel">
                    <div className="panel-hd">
                      {/* Each block in the strip below is one rate placed on
                          the nights it covers. The strip shows that; it does
                          not need a caption saying so. */}
                      <h2>{t('app.buildHeading')}</h2>
                    </div>
                    <div className="panel-bd">
                      <NightStrip
                        quote={quote}
                        arrival={arrival}
                        nights={nights}
                        hasRate={(id) => detail.programme.rates.some((r) => r.id === id)}
                        onExplain={explainRate}
                      />
                    </div>
                  </section>
                  <section className="panel">
                    <div className="panel-hd"><h2>{t('app.tryDatesHeading')}</h2></div>
                    <div className="panel-bd">
                      <PriceGrid
                        set={compiled.set} arrival={arrival} nights={nights} quantities={qty}
                        onPick={(a, n) => { setArrival(a); setNights(n); }}
                      />
                    </div>
                  </section>
                </>
              )}

              {sub === 'calendar' && (
                <section className="panel" id="night-calendar">
                  <div className="panel-hd">
                    <h2>{t('calendar.heading')}</h2>
                    <span className="grow" />
                    {detail.state === 'published' && (
                      <span className="chip">{t('matrix.publishedChip')}</span>
                    )}
                  </div>
                  <div className="panel-bd">
                    <p className="note">{t('calendar.intro')}</p>
                    {/* Said where the numbers are, not in a help page. The
                        figures here are what the operator SETS; the tax and the
                        cleaning fee are added afterwards and cannot be edited
                        from this screen. */}
                    <p className="note">{t('calendar.excludesExtras')}</p>
                    <NightCalendar
                      programme={detail.programme}
                      periods={periodsForYear}
                      seasonYear={detail.seasonYear}
                      readOnly={detail.state === 'published'}
                      onChange={(next) => {
                        // Saved immediately rather than held as a draft: a
                        // calendar edit is one deliberate act on a named set of
                        // nights, not a sweep across a grid, and leaving it
                        // unsaved would mean the recomputed calendar disagreed
                        // with what is stored.
                        void run(async () => {
                          await api.updateVersion(detail.id, { programme: next });
                          const v = await api.version(detail.id);
                          setDetail(v);
                          await reload();
                          return v;
                        }, t('app.programmeSaved'));
                      }}
                    />
                  </div>
                </section>
              )}

              {sub === 'ladder' && (
                <LadderReview
                  steps={steps}
                  /* Re-read the rungs off the card as it is NOW, discarding the
                     hand-edited ones. Offered rather than done: the operator's
                     own numbers are not something a background change gets to
                     throw away. */
                  basis={ladderBasis}
                  stale={ladderStale}
                  onReread={() => {
                    setStepsDirty(false);
                    setLadderStale(false);
                    seededFrom.current = null;
                    setDetail((d) => (d ? { ...d } : d));
                  }}
                  onSteps={(next) => { setStepsDirty(true); setSteps(next); }}
                  proposal={proposal}
                  busy={busy}
                  readOnly={detail.state === 'published'}
                  onPropose={() => {
                    void run(async () => {
                      const p = await api.proposeLadder(detail.id, steps);
                      setProposal(p);
                      return p;
                    });
                  }}
                  onApply={(accept) => {
                    void run(async () => {
                      await api.applyLadder(detail.id, steps, accept);
                      const v = await api.version(detail.id);
                      setDetail(v);
                      setProposal(null);
                      await reload();
                      return v;
                    }, t('app.appliedChanges', { count: accept.length }));
                  }}
                />
              )}

              {sub === 'programme' && (
                <>
                  {detail.drift.length > 0 && (
                    <section className="panel">
                      <div className="panel-hd">
                        <h2>{t('app.driftHeading')}</h2>
                        <span className="grow" />
                        <span className="chip">{detail.drift.length}</span>
                      </div>
                      <div className="panel-bd">
                        {/* Nothing is rewritten when a library period moves:
                            a published price must never shift because someone
                            edited the calendar. The screen reports the drift
                            and leaves the rates alone. */}
                        <p className="note" style={{ marginBottom: 10 }}>{t('app.driftNote')}</p>
                        <div className="scroll-x">
                          <table>
                            <thead>
                              <tr>
                                <th>{t('app.driftRate')}</th>
                                <th>{t('app.driftPeriod')}</th>
                                <th>{t('app.driftOnRate')}</th>
                                <th>{t('app.driftInLibrary')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {detail.drift.map((d) => (
                                <tr key={d.rateId}>
                                  <td>{d.rateId}</td>
                                  <td>{d.periodLabel}</td>
                                  <td className="num">{d.rate.from} → {d.rate.until}</td>
                                  <td className="num">{d.library.from} → {d.library.until}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </section>
                  )}
                  <div className="cols">
                    {/* Sticky, because the right-hand column got tall.
                        The rate list is a fixed 440px and the detail beside it
                        is now a price panel plus the whole shape editor, so
                        scrolling down to change a window left a column of
                        nothing where the list had been. Following the scroll
                        also means the next rate is one click away instead of one
                        click and a scroll back up. */}
                    <section className="panel col-sticky">
                      <div className="panel-hd">
                        <h2>{t('app.ratesHeading')}</h2>
                        <span className="grow" />
                        <span className="chip">{detail.programme.rates.length}</span>
                      </div>
                      <div className="panel-bd">
                        <RateList
                          programme={detail.programme}
                          diagnostics={compiled.diagnostics}
                          selected={selectedRate}
                          onAdd={detail.state === 'published' ? undefined : (role) => {
                            /**
                             * A new rule, opened for editing.
                             *
                             * Its window is the season the operator is looking
                             * at, not today: a rule added while authoring 2027
                             * that quietly covers 2026 is one nobody would
                             * think to check.
                             */
                            const id = `rule-${role}-${Date.now().toString(36)}`;
                            const rate = {
                              id,
                              label: t('app.newRuleName'),
                              role,
                              nights: role === 'nightly' ? 1 : 7,
                              price: { base: 0 },
                              active: { from: `${year}-04-01`, until: `${year}-11-01` },
                            } as Programme['rates'][number];
                            setDetail({
                              ...detail,
                              programme: {
                                ...detail.programme,
                                rates: [...detail.programme.rates, rate],
                              },
                            });
                            setSelectedRate(id);
                            setRateDirty(true);
                          }}
                          onSelect={(id) => {
                            // Same reason: the panel's unsaved marker is about
                            // THIS rate, so selecting another must not carry it.
                            setSelectedRate(id);
                            setRateDirty(false);
                          }}
                        />
                      </div>
                    </section>
                    <div className="stack">
                      {/* Card settings: the two things that belong to the CARD
                          and not to any one rule. Both were reachable only by
                          editing JSON — and copying, not even by that: the API
                          route existed with no button in front of it, which is
                          exactly what the operator asked about. */}
                      {detail.state !== 'published' && (
                        <section className="panel">
                          <div className="panel-hd"><h2>{t('copy.heading')}</h2></div>
                          <div className="panel-bd stack-sm">
                            <RoundingSetting
                              programme={detail.programme}
                              onChange={(next) => {
                                setDetail({ ...detail, programme: next });
                                setRateDirty(true);
                              }}
                            />
                            <CopyRates
                              target={detail.programme}
                              busy={busy}
                              sources={copySources}
                              sourceId={copyFrom}
                              sourceProgramme={copySource?.programme ?? null}
                              onPickSource={(id) => {
                                setCopyFrom(id);
                                setCopySource(null);
                                if (!id) return;
                                void run(async () => {
                                  const v = await api.version(id);
                                  setCopySource(v);
                                  return v;
                                });
                              }}
                              onCopy={(rateIds) => {
                                void run(async () => {
                                  /**
                                   * Server-side, not a local merge. The route
                                   * compiles the result and refuses a card that
                                   * would not price — and the operator's next
                                   * act after copying is to publish.
                                   */
                                  await api.copyRates(detail.id, copyFrom, rateIds);
                                  const v = await api.version(detail.id);
                                  setDetail(v);
                                  setRateDirty(false);
                                  await reload();
                                  return v;
                                }, t('copy.copied', { count: rateIds.length }));
                              }}
                            />
                          </div>
                        </section>
                      )}
                      <section className="panel">
                        <div className="panel-hd">
                          <h2>{t('app.checksHeading')}</h2>
                          <span className="grow" />
                          <span className={`chip${compiled.ok ? '' : ' bad'}`}>
                            {compiled.diagnostics.length
                              ? t('app.checksToReview', { count: compiled.diagnostics.length })
                              : t('app.checksAllClear')}
                          </span>
                        </div>
                        <div className="panel-bd"><Diagnostics diagnostics={compiled.diagnostics} /></div>
                      </section>
                      {selectedRate && (
                        <section className="panel" id="rate-detail">
                          <div className="panel-hd">
                            <h2>
                              {/* The label an operator gave it, with the id
                                  underneath. The id used to be the heading, and
                                  `comfort-voorseizoenpakket` is not what anybody
                                  calls the pre-season package. */}
                              {detail.programme.rates.find((r) => r.id === selectedRate)?.label
                                ?? selectedRate}
                            </h2>
                            <span className="grow" />
                            <span className="chip mono">{selectedRate}</span>
                            {/* The only way to remove a rule was the raw JSON
                                editor, or the price grid if the rule happened
                                to be a cell in it. An operator who added a rate
                                by mistake had to hand-edit a programme to undo
                                it. Held locally like every other edit here, so
                                Save is still the commit and Revert still
                                undoes it. */}
                            {detail.state !== 'published' && (
                              <button
                                type="button"
                                className="btn danger"
                                onClick={() => {
                                  const rate = detail.programme.rates
                                    .find((r) => r.id === selectedRate);
                                  // Named, because "are you sure" answers a
                                  // question nobody asked. The name is what
                                  // tells an operator whether this is the rate
                                  // they meant.
                                  if (!window.confirm(t('app.deleteRateConfirm', {
                                    label: rate?.label ?? selectedRate,
                                  }))) return;
                                  setDetail({
                                    ...detail,
                                    programme: {
                                      ...detail.programme,
                                      rates: detail.programme.rates
                                        .filter((r) => r.id !== selectedRate),
                                    },
                                  });
                                  setSelectedRate(null);
                                  setRateDirty(true);
                                }}
                              >
                                {t('app.deleteRate')}
                              </button>
                            )}
                          </div>
                          <div className="panel-bd">
                            <RateDetail
                              rate={detail.programme.rates.find((r) => r.id === selectedRate)!}
                              readOnly={detail.state === 'published'}
                              /**
                               * The card's OWN season, not the one in the
                               * header. An operator can open a 2026 card while
                               * the header says 2027, and offering next year's
                               * Ascension to fill this year's window is how a
                               * rate ends up twelve months out with nothing on
                               * screen admitting it.
                               */
                              periods={periods.filter((p) => p.year === detail.seasonYear)}
                              quantities={detail.programme.quantities}
                              onChange={(next) => {
                                // Held locally until Save, like the price grid.
                                // Writing on every keystroke would put a request
                                // behind each digit of "137".
                                setDetail({
                                  ...detail,
                                  programme: {
                                    ...detail.programme,
                                    rates: detail.programme.rates.map(
                                      (r) => (r.id === selectedRate ? next : r),
                                    ),
                                  },
                                });
                                setRateDirty(true);
                              }}
                            />
                            {detail.state === 'published' && (
                              <p className="note">{t('app.publishedIsReadOnly')}</p>
                            )}
                          </div>
                        </section>
                      )}
                      {/* OUTSIDE the selected-rate panel, because the unsaved
                          change belongs to the CARD and not to whichever rule
                          happens to be open. Inside it, deleting a rule — or
                          simply clicking it again to close it — took Save and
                          Revert away with the panel and stranded the edit. */}
                        {rateDirty && detail.state !== 'published' && (
                          <div className="row">
                            <button
                              type="button" className="btn primary" disabled={busy}
                              onClick={() => {
                                void run(async () => {
                                  await api.updateVersion(detail.id, {
                                    programme: detail.programme,
                                  });
                                  const v = await api.version(detail.id);
                                  setDetail(v);
                                  setRateDirty(false);
                                  await reload();
                                  return v;
                                }, t('app.programmeSaved'));
                              }}
                            >
                              {t('matrix.save')}
                            </button>
                            <button
                              type="button" className="btn"
                              onClick={() => {
                                void run(async () => {
                                  const v = await api.version(detail.id);
                                  setDetail(v);
                                  setRateDirty(false);
                                  return v;
                                });
                              }}
                            >
                              {t('app.revert')}
                            </button>
                            <span className="note">{t('app.rateUnsaved')}</span>
                          </div>
                        )}
                    </div>
                  </div>
                  {/*
                    COLLAPSED, and the reason matters more than the markup.

                    Sitting open under every card, a JSON textarea labelled
                    "Programme source" with an "Apply changes" button beside it
                    reads as the way to edit a rate — and no operator is going to
                    edit JSON. The amount fields above are the way; this is the
                    escape hatch for the shape of change they cannot express yet,
                    and an escape hatch should look like one.

                    A `details` element rather than a state toggle: it remembers
                    nothing, needs no wiring, and is closed on every visit, which
                    is the point.
                  */}
                  <details className="panel source-panel">
                    <summary className="panel-hd">
                      <h2>{t('app.programmeSourceHeading')}</h2>
                      <span className="grow" />
                      <span className="chip">{t('app.programmeSourceAdvanced')}</span>
                    </summary>
                    <div className="panel-bd">
                      <p className="note">{t('app.programmeSourceNote')}</p>
                      <ProgrammeEditor
                        accommodationKey={detail.id}
                        programme={detail.programme}
                        dirty={detail.state !== 'published'}
                        onRevert={() => { void run(async () => { const v = await api.version(detail.id); setDetail(v); return v; }); }}
                        onApply={(p) => {
                          if (detail.state === 'published') {
                            return t('app.publishedIsReadOnly');
                          }
                          void run(async () => {
                            await api.updateVersion(detail.id, { programme: p });
                            const v = await api.version(detail.id);
                            setDetail(v);
                            await reload();
                            return v;
                          }, t('app.programmeSaved'));
                          return t('app.saving');
                        }}
                      />
                    </div>
                  </details>
                </>
              )}

              {sub === 'guests' && (
                <Guests
                  programme={detail.programme}
                  readOnly={detail.state === 'published'}
                  onChange={(p) => {
                    if (detail.state === 'published') return;
                    // Optimistic, then reconciled from the server's own view —
                    // these are checkboxes and number fields, and waiting a
                    // round trip before a tick appears makes them feel broken.
                    setDetail({ ...detail, programme: p });
                    void run(async () => {
                      await api.updateVersion(detail.id, { programme: p });
                      const v = await api.version(detail.id);
                      setDetail(v);
                      return v;
                    });
                  }}
                />
              )}

              {sub === 'published' && (
                <Published programme={detail.programme} quantities={qty} />
              )}
            </>
          )}
        </>
      )}

      {/* Units and blocks own their own loading: a park is a few hundred rows
          that only this screen and the enquiry list need, and fetching them on
          first paint would slow down the screen every operator opens first. */}
      {tab === 'units' && (
        <Units
          accommodations={accommodations}
          accommodationId={activeAcc}
          onAccommodation={(id) => {
            setActiveAcc(id || null);
            const first = versions.find((v) => v.accommodationId === id);
            setActiveVersion(first?.id ?? null);
          }}
          run={run}
          busy={busy}
        />
      )}

      {/* Loads its own rules, for the same reason units do: a handful of rows
          that only this screen needs, and the price grid is what opens first. */}
      {tab === 'rules' && (
        <Rules accommodations={accommodations} year={year} run={run} busy={busy} />
      )}

      {tab === 'inquiries' && (
        <Inquiries accommodations={accommodations} run={run} busy={busy} />
      )}

      {/* `onSaved` rather than a refetch: the formatters are derived from this
          state, so the rest of the dashboard has to re-render with the new
          settings the moment they are saved and not on the next visit. */}
      {tab === 'settings' && (
        <Settings run={run} onSaved={setSettings} busy={busy} />
      )}

      {tab === 'accounts' && <Accounts run={run} busy={busy} meId={meId} />}

      <ShortcutHelp open={help} onClose={() => setHelp(false)} />
      {/* There was a footer here explaining the split: the API owns the data,
          this browser owns the compute, and versions and publish state are
          shared between operators so they cannot live client-side — while
          quoting stays local and instant, by the same engine that agrees with
          the legacy calculator on all 115,385 differential stays. True, and
          the reason the app is shaped this way, but nothing an operator does
          anything with. It lives here now. */}
    </main>
  );
}

function firstActive(p: Programme): number {
  let lo: number | null = null;
  for (const r of p.rates) {
    const f = toDay(r.active.from);
    if (f !== null && (lo === null || f < lo)) lo = f;
  }
  return lo ?? Math.round(Date.now() / 86_400_000);
}
